/**
 * Deutsche Beschriftungen für den Austausch mit dem Kanalmanager.
 *
 * <p>Die Zustände und Vorgangsnamen kommen roh vom Vendor. Sie stehen an drei Stellen — Zeile,
 * Zusammenfassung und Detailfenster —, und genau wie in `systemLabels.ts` (Block D/E) gibt es
 * sie deshalb genau einmal.</p>
 *
 * <p>Die Tabellen sind gegen die Quellen geschrieben, nicht geraten: `ChannexEvents` und
 * `ChannexWebhookProcessor` für die eingehenden Meldungen, die drei `AuditLog.Action`-Werte aus
 * `ChannexInsightsService.SyncActivity` für die ausgehenden Übertragungen. Unbekanntes fällt
 * ehrlich auf den Rohwert zurück, statt eine Bedeutung zu erfinden.</p>
 */

const STATUS_LABEL: Record<string, string> = {
  processed: 'Verarbeitet',
  failed: 'Gescheitert',
  parked: 'Zurückgestellt',
  retryable: 'Wird wiederholt',
  dead_lettered: 'Endgültig gescheitert',
  received: 'Empfangen',
  pending: 'In Arbeit',
  // Der häufigste Zustand im Betrieb: das Echo unserer eigenen Übertragung. Er stand roh in
  // jeder zweiten Zeile — „ignored" beantwortet dort keine Frage.
  ignored: 'Ohne Wirkung',
  noted: 'Vermerkt',
  alerted: 'Gemeldet',
};

const EVENT_LABEL: Record<string, string> = {
  // Eingehende Meldungen (ChannexEvents).
  ari: 'Preise und Verfügbarkeit',
  booking_new: 'Neue Buchung',
  booking_modification: 'Buchung geändert',
  booking_cancellation: 'Buchung storniert',
  booking_unmapped_room: 'Buchung ohne zugeordnetes Zimmer',
  booking_unmapped_rate: 'Buchung ohne zugeordnete Rate',
  non_acked_booking: 'Buchung ohne Bestätigung',
  message: 'Nachricht',
  sync_error: 'Fehler beim Abgleich',
  sync_warning: 'Warnung beim Abgleich',
  review: 'Bewertung',
  updated_review: 'Bewertung geändert',
  new_channel: 'Portal verbunden',
  updated_channel: 'Portal geändert',
  disconnect_channel: 'Portal getrennt',
  activate_channel: 'Portal aktiviert',
  deactivate_channel: 'Portal deaktiviert',
  reservation_request: 'Buchungsanfrage',
  alteration_request: 'Änderungswunsch',
  inquiry: 'Anfrage',
  accepted_reservation: 'Anfrage angenommen',
  declined_reservation: 'Anfrage abgelehnt',
  rate_error: 'Preis vom Portal abgelehnt',
  disconnect_listing: 'Inserat getrennt',
  // Ausgehende Übertragungen (AuditLog.Action).
  AriPush: 'Preise und Verfügbarkeit übertragen',
  AriPushBatch: 'Mehrere Zeiträume übertragen',
  ChannexPropertyUpdatePushed: 'Apartmentdaten übertragen',
};

export function syncStatusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status;
}

export function syncEventLabel(eventType: string): string {
  return EVENT_LABEL[eventType] ?? eventType;
}

/** Ausgehende Übertragung oder eingehende Meldung — die Richtung ist die erste Frage der Zeile. */
export function syncDirectionLabel(source: string): string {
  return source === 'ari-push' ? 'Ausgehend' : 'Eingehend';
}

/** Herkunft des Detaileintrags: unser eigenes Protokoll oder die Meldung des Kanalmanagers. */
export function syncReferenceLabel(referenceKind: string | undefined): string {
  return referenceKind === 'audit' ? 'Eigenes Protokoll' : 'Meldung des Kanalmanagers';
}
